import clsx from "clsx";

import { InformationCircleIcon } from "@heroicons/react/24/outline";

import { Toggle } from "../base/Toggle";
import { ITitleConfigRequest } from "../../models/Category/TitleConfig";

const MAX_PROMPT_LENGTH = 4000;

interface DamagePromptPanelProps {
  categoryName: string;
  config: ITitleConfigRequest;
  onChange: (patch: Partial<ITitleConfigRequest>) => void;
}

export default function DamagePromptPanel({
  categoryName,
  config,
  onChange,
}: DamagePromptPanelProps) {
  const length = config.damagePrompt?.length ?? 0;
  const overLimit = length > MAX_PROMPT_LENGTH;

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">
        Damage Description Prompt
      </h2>

      <Toggle
        labelRight
        className="mb-4"
        label="Use category-specific damage prompt"
        checked={config.useCategoryDamagePrompt}
        onChange={(value) => onChange({ useCategoryDamagePrompt: value })}
      />

      <label className="block text-sm font-medium opacity-80 mb-1">
        Prompt for {categoryName}
      </label>
      <textarea
        rows={10}
        disabled={!config.useCategoryDamagePrompt}
        value={config.damagePrompt ?? ""}
        onChange={(e) => onChange({ damagePrompt: e.currentTarget.value })}
        placeholder={`Describe the visible condition of the ${categoryName.toLowerCase()} only using evidence in the photos.`}
        className={clsx(
          "w-full rounded border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-600",
          overLimit ? "border-red-500" : "border-orange-600",
          !config.useCategoryDamagePrompt &&
            "bg-gray-100 text-gray-500 cursor-not-allowed",
        )}
      />
      <div
        className={clsx(
          "mt-1 text-right text-xs",
          overLimit ? "text-red-600" : "text-gray-500",
        )}
      >
        {length} / {MAX_PROMPT_LENGTH}
      </div>

      <div className="mt-3">
        <Toggle
          labelRight
          label="Include standard condition rules"
          checked={config.includeStandardConditionRules}
          disabled={!config.useCategoryDamagePrompt}
          onChange={(value) =>
            onChange({ includeStandardConditionRules: value })
          }
        />
        <p className="mt-1 text-xs text-gray-500">
          Appends the shared rules: describe only what is visible, never infer
          unseen damage, no fitment guarantees.
        </p>
      </div>

      <div className="mt-3 flex gap-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
        <InformationCircleIcon className="h-4 w-4 shrink-0 text-gray-400" />
        <span>
          Used by AI to generate condition descriptions from part photos in
          this category. Categories with this turned off have no condition
          description generated at all.
        </span>
      </div>
    </section>
  );
}
